import {
  AfterViewInit,
  ChangeDetectorRef,
  Component,
  HostListener,
  TemplateRef,
  viewChild,
  inject,
  DOCUMENT
} from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { TranslateService, TranslatePipe, TranslateDirective } from '@ngx-translate/core';
import {
  ModalService,
  ReactiveComponent,
  TwoButtonModalV2,
  TwoButtonModalV2Component,
  unique,
  BackendResponse,
  ToastV2Service,
  SpinnerV2Service,
  ControlButton,
  GridColumnV2,
  GridStateChangeV2,
  deepClone,
  UserRightsService,
  NotificationsV2Module,
  ButtonV2Module,
  RadioV2Module,
  GridV2Module,
  ServiceErrorV2Component,
} from '@schenck/rotec-components';
import { filter, switchMap, takeUntil, tap } from 'rxjs/operators';
import { translateComponent } from 'src/components/components-and-function-group';
import { StatusFilterComponent } from 'src/components/status-filter/status-filter.component';
import { createSchedulingFilter } from 'src/components/task-interval.pipe';
import { addTitleAndWidthToGridColumn, PartialColumn } from 'src/pages/config-utils';
import { routes } from 'src/routes';
import { DownloadService, MachineTaskService } from 'src/services';
import { ChooseMachineTaskOptionModalComponent } from '../choose-machine-task-option-modal/choose-machine-task-option-modal.component';
import { MachineTaskSearchService } from '../machine-task-search.service';
import { machineTaskColumnsBaseConfig } from '../machine-task-settings.api';
import { MachineTaskSettingsService } from '../machine-task-settings.service';
import { MachineTask } from '../machine-tasks.model';
import { MachineTaskModalComponent } from '../task-modal/machine-task-modal.component';
import { AsyncPipe } from '@angular/common';
import { PRIORITY_ITEMS } from 'src/utils/constants/upsert-task.const';
import { UserRightsActions } from 'src/utils/enums/user-rights.enum';
import { UpsertMachineTaskWizardComponent } from '../upsert-machine-task-wizard/upsert-machine-task-wizard.component';
import { UserRole } from 'src/utils/enums/user-roles.enum';
import { PriorityComponent } from '../../../components/priority/priority.component';
import { DateComponent } from '../../../components/date/date.component';
import { StatusComponent } from '../../../components/status/status.component';
import { StatusFilterComponent as StatusFilterComponent_1 } from '../../../components/status-filter/status-filter.component';
import { TaskIntervalPipe } from '../../../components/task-interval.pipe';
import { TypeEnumPipe } from '../../../components/type-enum.pipe';
import { TaskComponentPipe } from '../../../components/components-and-function-group';

@Component({
  selector: 'ma-machine-tasks',
  templateUrl: './machine-tasks.component.html',
  styleUrls: ['./machine-tasks.component.less'],
  imports: [
    NotificationsV2Module,
    TranslatePipe,
    TranslateDirective,
    ButtonV2Module,
    RadioV2Module,
    GridV2Module,
    PriorityComponent,
    DateComponent,
    StatusComponent,
    StatusFilterComponent_1,
    AsyncPipe,
    TaskIntervalPipe,
    TypeEnumPipe,
    TaskComponentPipe,
    ServiceErrorV2Component,
  ],
})
export class MachineTasksComponent extends ReactiveComponent implements AfterViewInit {
  private readonly document = inject<Document>(DOCUMENT);
  private readonly cdr = inject(ChangeDetectorRef);
  private readonly modalService = inject(ModalService);
  private readonly downloadService = inject(DownloadService);
  private readonly router = inject(ActivatedRoute);
  private readonly translate = inject(TranslateService);
  private readonly taskService = inject(MachineTaskService);
  private readonly toastService = inject(ToastV2Service);
  private readonly spinnerService = inject(SpinnerV2Service);
  public settingsService = inject(MachineTaskSettingsService);
  public searchService = inject(MachineTaskSearchService);
  public userRightsService = inject(UserRightsService);

  readonly statusFilter = viewChild<TemplateRef<StatusFilterComponent>>('statusFilter');
  gridHeight = 500;
  hasPagination = false;
  public selectedElement: string;
  state = this.connect(
    {
      searchData: this.searchService.state$,
      columns: this.onInit$.pipe(switchMap(() => this.createColumns())),
      responsibleCompanyFilterValue: this.searchService.responsibleCompanyFilterValue$,
    },
    this.cdr
  );

  public controlButtons: ControlButton[] = [
    {
      icon: 'trash',
      click: (_) => {
        this.deleteTasks();
      },
    },
    {
      icon: 'file-pdf',
      label: 'COMMON_Export',
      click: (_) => {
        this.export();
      },
    },
  ];
  public actions = UserRightsActions;

  constructor() {
    super();
    // search for Machine if in Route Params:
    const serial = this.router.snapshot.queryParamMap.get(routes.machineTasks.params.serial);
    if (serial) {
      this.searchService.search({ filter: [{ key: 'machineSerial', value: [serial] }] });
    }

    this.userRightsService
      .hasActionRights$(UserRightsActions.deleteTask)
      .pipe(
        takeUntil(this.onDestroy$),
        filter((actions) => typeof actions === 'boolean')
      )
      .subscribe((hasDeleteRights) => {
        this.controlButtons = this.controlButtons.filter(({ icon }) => {
          switch (icon) {
            case 'trash':
              return hasDeleteRights;
            default:
              return true;
          }
        });
      });
  }

  ngAfterViewInit() {
    super.ngAfterViewInit();
    this.setGridHeight();

    this.searchService.state$.pipe(takeUntil(this.onDestroy$)).subscribe((state) => {
      this.hasPagination = state.total > state.pageSize;
      this.setGridHeight();
    });
  }

  @HostListener('window:resize') public setGridHeight() {
    const header = this.document.querySelector('.header-row');
    const titleHeight = 75;
    const navbarHeight = this.document.querySelector('.pages-navbar')?.clientHeight;
    const paginationSize = this.hasPagination ? 80 : 0;
    this.gridHeight =
      window.innerHeight - titleHeight - navbarHeight - header.clientHeight - 250 - paginationSize;
  }

  refineSearch(state: GridStateChangeV2) {
    this.searchService.search({
      filter: <any>state.filter,
      fulltext: state.fulltext,
      page: state.page,
      sort: <any>state.sort,
    });
  }

  public reactToPageSizeChange(pageSize: number) {
    this.searchService.search({ pageSize });
  }

  keyupEnterSearch(searchText: string) {
    this.searchService.search({ fulltext: searchText, page: 0 });
  }

  updateColumnSettings(columns: GridColumnV2[]) {
    this.settingsService.updateColumnSettings(columns);
  }

  openTask(task: MachineTask) {
    this.modalService.open<string>(MachineTaskModalComponent, task.id);
  }

  toggleSelectedItem($event) {
    this.selectedElement = $event?.id;
  }

  addNewTask() {
    this.userRightsService.userRole$.pipe(takeUntil(this.onDestroy$)).subscribe((roleName) => {
      roleName === UserRole.Supervisor
        ? this.modalService.open(UpsertMachineTaskWizardComponent)
        : this.modalService.open(ChooseMachineTaskOptionModalComponent);
    });
  }

  export() {
    this.downloadService.downloadNonExecutedMachineTaskPDF(this.state.searchData.selectedIds);
  }

  deleteTasks() {
    this.modalService
      .open<TwoButtonModalV2, boolean>(TwoButtonModalV2Component, {
        confirmText: 'Delete',
        cancelText: 'Cancel',
        title: '',
        text: 'The following tasks (series) will be deleted: VALUE',
        textParams: {
          value: unique(this.searchService.getSelectedResults().map((res) => res.title)).join(', '),
        },
        summary: 'Delete Tasks',
        darkMode: true,
        warning: true,
      })
      .events$.pipe(
        takeUntil(this.onDestroy$),
        filter((wantsToDelete) => !!wantsToDelete),
        tap(() => this.spinnerService.setLoading(this.searchService.getSettingsKey(), true)),
        switchMap(() => this.taskService.deleteMultiple$(this.searchService.state.selectedIds))
      )
      .subscribe((responses) => {
        this.searchService.setSelectedResults([]);
        if (
          responses.some(
            (response) =>
              !BackendResponse.isSuccess(response) && !BackendResponse.isNotFound(response)
          )
        ) {
          this.toastService.open({
            type: 'error',
            text: 'TECHNICAL_ERROR_try again later or contact admin',
          });
        }
      });
  }

  private createColumns() {
    const columnDefs: PartialColumn[] = [
      {
        key: 'plantName',
        sortable: true,
        filter: {
          type: 'multiselect',
          showSearchInput: true,
          items: this.getItemSource('plantName'),
        },
      },
      {
        key: 'machineName',
        sortable: true,
        filter: { type: 'multiselect', items: this.getItemSource('machineName') },
      },
      {
        key: 'machineSerial',
        sortable: true,
        filter: { type: 'multiselect', items: this.getItemSource('machineSerial') },
      },
      {
        key: 'schenckId',
        sortable: true,
        filter: {
          type: 'text',
        },
      },
      {
        key: 'customerId',
        sortable: true,
        filter: {
          type: 'text',
        },
      },
      {
        key: 'title',
        sortable: true,
        filter: {
          type: 'text',
        },
      },
      {
        key: 'priority',
        sortable: true,
        filter: {
          type: 'checkbox',
          showAllCheckbox: true,
          options: deepClone(PRIORITY_ITEMS),
        },
      },
      {
        key: 'responsibleCompany',
        sortable: true,
        filter: {
          type: 'radio',
          options: [
            { value: 'Customer', name: 'My' },
            { value: 'Schenck', name: 'Schenck' },
          ],
          displayAllOption: true,
        },
      },
      {
        key: 'responsible',
        sortable: true,
        filter: {
          type: 'multiselect',
          items: this.getItemSource('responsible'),
        },
      },
      {
        key: 'executive',
        sortable: true,
        filter: {
          type: 'multiselect',
          items: this.getItemSource('executive'),
        },
      },
      {
        key: 'dueDate',
        sortable: true,
        filter: { type: 'date' },
      },
      {
        key: 'status',
        sortable: true,
        filter: {
          type: 'template',
          template: this.statusFilter(),
          displayValueFn: StatusFilterComponent.filterforStatus,
          deleteFilterFn: StatusFilterComponent.deleteStatusFilter,
        },
      },
      {
        key: 'description',
        sortable: true,
        filter: {
          type: 'text',
        },
      },
      {
        key: 'component',
        sortable: true,
        filter: {
          type: 'multiselect',
          items: this.getItemSource('component', (value) => {
            return translateComponent(value, this.translate);
          }),
        },
      },
      {
        key: 'type',
        sortable: true,
        filter: { type: 'multiselect', items: this.getItemSource('type'), withCustomValue: false },
      },
      {
        key: 'requiredTooling',
        sortable: true,
        filter: {
          type: 'text',
        },
      },
      {
        key: 'possibleSpareParts',
        sortable: true,
        filter: {
          type: 'text',
        },
      },
      {
        key: 'expectedDurationMinutes',
        sortable: true,
      },
      {
        key: 'scheduling',
        sortable: true,
        filter: createSchedulingFilter(this.searchService.allResults$, this.translate),
      },
    ];

    return this.settingsService.createColumns$(
      addTitleAndWidthToGridColumn(columnDefs, machineTaskColumnsBaseConfig)
    );
  }

  private getItemSource(entity: keyof MachineTask, translateFn?: (value: any) => string) {
    return this.searchService.getMultiselectItemSource(entity, translateFn);
  }

  public isSelected(id: string): boolean {
    return this.state.searchData.selectedIds.includes(id) || this.selectedElement === id;
  }
}
